import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import apiClient from '../api/axios';

const CONTACT_EMAIL = 'kirathaent@gmail.com';

const PLAN_META = {
  STARTER: { labelKey: 'starter' },
  GROWTH: { labelKey: 'growth' },
  ENTERPRISE: { labelKey: 'enterprise' },
};

function PricingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['pricing', 'common']);
  const [pricing, setPricing] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadPricing = () => {
    setLoading(true);
    setError(false);
    apiClient.get('/api/public/pricing')
      .then((res) => setPricing(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPricing();
  }, []);

  const commonFeatures = t('pricing:commonFeatures', { returnObjects: true });

  const handlePlanClick = (planKey) => {
    if (planKey === 'ENTERPRISE') {
      const subject = encodeURIComponent(t('pricing:enterprise.ctaSubject'));
      window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}`;
      return;
    }
    navigate(`/signup?plan=${planKey}`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 px-4 py-10">
      <div className="w-full max-w-5xl">
        {/* Brand header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-extrabold tracking-tight">
            <span style={{ color: '#085041' }}>Bento</span>
            <span className="text-orange-600">Ping</span>
          </h1>
          <p className="text-sm text-gray-600 mt-1">{t('common:tagline')}</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-orange-100/60 border border-orange-100 p-8">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">{t('pricing:title')}</h2>
              <p className="text-sm text-gray-500">
                {!loading && !error && pricing
                  ? t('pricing:subtitle', { days: pricing.trialDays })
                  : ' '}
              </p>
            </div>
            <LanguageSwitcher className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-600 bg-gray-50 hover:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-200 cursor-pointer" />
          </div>

          {loading && (
            <p className="text-center text-sm text-gray-500 py-12">{t('common:loading')}</p>
          )}

          {!loading && error && (
            <div className="text-center py-12">
              <p className="text-sm text-red-600 mb-3">{t('pricing:loadError')}</p>
              <button
                type="button"
                onClick={loadPricing}
                className="rounded-lg border border-orange-300 px-4 py-2 text-sm font-semibold text-orange-700 hover:bg-orange-50"
              >
                {t('pricing:retry')}
              </button>
            </div>
          )}

          {!loading && !error && pricing && (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {pricing.plans.map((plan) => {
                const meta = PLAN_META[plan.key];
                const isPopular = plan.popular;
                const isEnterprise = plan.key === 'ENTERPRISE';
                const features = [...commonFeatures];
                if (plan.key === 'GROWTH') {
                  features.push(t('pricing:growth.extraFeature'));
                }

                return (
                  <div
                    key={plan.key}
                    className={
                      isPopular
                        ? 'relative flex flex-col rounded-xl border-2 border-orange-500 bg-orange-50/40 p-6 shadow-md shadow-orange-200'
                        : 'relative flex flex-col rounded-xl border-2 border-gray-200 bg-white p-6'
                    }
                  >
                    {isPopular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center rounded-full bg-orange-100 px-3 py-0.5 text-xs font-semibold text-orange-700 border border-orange-200">
                        {t('pricing:mostPopular')}
                      </span>
                    )}

                    <h3 className="text-lg font-bold text-gray-900 text-center mt-2">
                      {t(`pricing:${meta.labelKey}.name`)}
                    </h3>

                    <p className="text-center mt-2">
                      {isEnterprise ? (
                        <span className="text-2xl font-extrabold text-gray-900">
                          {t('pricing:enterprise.priceLabel')}
                        </span>
                      ) : (
                        <>
                          <span className="text-2xl font-extrabold text-gray-900">
                            {'₹'}{plan.priceInr.toLocaleString('en-IN')}
                          </span>
                          <span className="text-sm text-gray-500">{t('pricing:perMonth')}</span>
                        </>
                      )}
                    </p>

                    <p className="text-center text-sm text-gray-600 mt-1">
                      {t(`pricing:${meta.labelKey}.staffCap`, { count: plan.staffCap })}
                    </p>

                    <ul className="mt-4 space-y-2 flex-1">
                      {features.map((feature) => (
                        <li key={feature} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="text-orange-500 mt-0.5">&#10003;</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      type="button"
                      onClick={() => handlePlanClick(plan.key)}
                      className={
                        isPopular
                          ? 'mt-6 w-full rounded-xl border-2 border-orange-500 bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-3 text-base font-bold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2'
                          : 'mt-6 w-full rounded-xl border-2 border-gray-200 bg-white px-6 py-3 text-base font-semibold text-gray-800 shadow-sm transition hover:border-orange-400 hover:text-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:ring-offset-2'
                      }
                    >
                      {t(`pricing:${meta.labelKey}.cta`)}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          {t('common:footer', { year: new Date().getFullYear() })}
        </p>
      </div>
    </div>
  );
}

export default PricingPage;
